import React, { useEffect, useState } from "react";
import axios from "axios";
import { backend_url } from "../App";
import "./CSS/varietyAccessories.css";
import { menuItems } from "../Constants/menuItems";
import { isAdmin } from "../utils/roleUtils";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import MediaField from "../Components/MediaField/MediaField";
import DownloadButton from "../Components/MediaField/DownloadButton";
import { MAX_IMAGE_MB } from "../utils/media";

const NewArrivals = () => {
  const [newArrivals, setNewArrivals] = useState([]);
  const [formData, setFormData] = useState({
    collectionName: "",
    category: "",
    routeTo: "",
    image: null,
  });
  const [showConfirm, setShowConfirm] = useState(false);
  const [selectedVideoId, setSelectedVideoId] = useState(null);
  const [selectedCategory, setSelectedCategory] = useState("");
  const [categoryOptions, setCategoryOptions] = useState([]);
  const [editingId, setEditingId] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [existingImage, setExistingImage] = useState("");

  const handleDelete = (_id) => {
    setSelectedVideoId(_id);
    setShowConfirm(true);
  };

  // Handle delete
  const confirmDelete = async (id) => {
    try {
      await axios.delete(`${backend_url}/api/newarrivals/${id}`);
      setShowConfirm(false);
      toast.success("New Arrival deleted Successfully");
      fetchNewArrivals(); //Refresh after deletion
    } catch (error) {
      toast.error("Error deleting arrival:", error);
    }
  };

  useEffect(() => {
    if (formData.routeTo && categoryOptions.length > 0) {
      const match = categoryOptions.find(
        (opt) => opt.value === formData.routeTo
      );
      if (!match) {
        setFormData((prev) => ({ ...prev, routeTo: "" }));
      }
    }
  }, [categoryOptions]);

  // Fetch all new arrivals
  useEffect(() => {
    fetchNewArrivals();
  }, []);

  const fetchNewArrivals = async () => {
    try {
      const response = await axios.get(`${backend_url}/api/newarrivals`);
      setNewArrivals(response.data);
    } catch (error) {
      toast.error("Error fetching newarrivals:", error);
    }
  };

  // Handle form input change
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
  };

  // Handle category change and filter route options
  const handleCategoryChange = (e) => {
    const selected = e.target.value;
    setSelectedCategory(selected);
    setFormData({ ...formData, category: selected });

    if (selected) {
      const selectedCategory = menuItems.find(
        (item) => item.value === selected
      );

      if (selectedCategory && selectedCategory.dropdown) {
        // Flatten dropdown and subcategories
        const options = selectedCategory.dropdown.flatMap((item) => {
          if (item.value === null) {
            return [
              { label: `-- ${item.label} --`, value: null },
              ...(item.subcategories || []),
            ];
          } else {
            return item;
          }
        });

        setCategoryOptions(options);
      } else {
        setCategoryOptions([]);
      }
    } else {
      setCategoryOptions([]);
    }
  };

  // Add or update variety
  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.image && !existingImage) {
      toast.error("Please choose an image");
      return;
    }

    const data = new FormData();
    data.append("collectionName", formData.collectionName);
    data.append("routeTo", formData.routeTo);
    data.append("image", formData.image || existingImage);
    data.append("category", formData.category);

    try {
      if (editingId) {
        await axios.put(`${backend_url}/api/newarrivals/${editingId}`, data);
        toast.success("Arrival Updated Successfully");
      } else {
        await axios.post(`${backend_url}/api/newarrivals`, data);
        toast.success("Arrival Added Successfully");
      }
      fetchNewArrivals();
      closeModal();
    } catch (error) {
      toast.error(error.response?.data?.error || "Error saving new arrival");
    }
  };

  const handleEdit = (newarrival) => {
    setFormData({
      collectionName: newarrival.collectionName,
      routeTo: newarrival.routeTo,
      image: null, // a new File only; the saved URL lives in existingImage
      category: newarrival.category,
    });
    setExistingImage(newarrival.image || "");

    setSelectedCategory(newarrival.category);

    // Use same dropdown logic as handleCategoryChange
    const selectedCategory = menuItems.find(
      (item) => item.value === newarrival.category
    );

    if (selectedCategory && selectedCategory.dropdown) {
      const options = selectedCategory.dropdown.flatMap((item) => {
        if (item.value === null) {
          return [
            { label: `-- ${item.label} --`, value: null },
            ...(item.subcategories || []),
          ];
        } else {
          return item;
        }
      });

      setCategoryOptions(options);
    } else {
      setCategoryOptions([]);
    }

    setEditingId(newarrival._id);
    setShowModal(true);
  };

  // Close modal and reset form
  const closeModal = () => {
    setShowModal(false);
    setEditingId(null);
    setFormData({ routeTo: "", image: null, category: "", collectionName: "" });
    setSelectedCategory("");
    setCategoryOptions([]);
    setExistingImage("");
  };

  const formatText = (text) =>
    text.replace(/-/g, " ").replace(/\b\w/g, (char) => char.toUpperCase());

  return (
    <>
      <div className="variety-admin">
        <div className="headerSections">
          <div className="head-title">New Arrivals</div>
          <button className="add-btn" onClick={() => setShowModal(true)}>
            + Add Arrival
          </button>
        </div>

        {/* Popup Modal */}
        {showModal && (
          <div className="modal-overlay" onClick={closeModal}>
            <div className="modal" onClick={(e) => e.stopPropagation()}>
              <h2>{editingId ? "Edit Arrival" : "Add Arrival"}</h2>
              <form onSubmit={handleSubmit} className="variety-form">
                <div className="form-group">
                  <label htmlFor="collectionName">Collection Name</label>
                  <input
                    type="text"
                    id="collectionName"
                    name="collectionName"
                    value={formData.collectionName}
                    onChange={handleChange}
                    maxLength={20}
                    placeholder="Enter Collection Name"
                    required
                  />
                </div>
                <div className="form-group">
                  <label htmlFor="category">Category</label>
                  <select
                    id="category"
                    name="category"
                    value={selectedCategory}
                    onChange={handleCategoryChange}
                    required
                  >
                    <option value="">Select a category</option>
                    {menuItems.map((category) => (
                      <option key={category.value} value={category.value}>
                        {category.label}
                      </option>
                    ))}
                  </select>
                </div>

                {/* Route To Dropdown */}
                <div className="form-group">
                  <label htmlFor="routeTo">Route To</label>
                  <select
                    id="routeTo"
                    name="routeTo"
                    value={formData.routeTo}
                    onChange={handleChange}
                    required
                    disabled={!selectedCategory}
                  >
                    <option value="">Select a route</option>
                    {categoryOptions.map((option) =>
                      option.value ? (
                        <option key={option.value} value={option.value}>
                          {option.label}
                        </option>
                      ) : (
                        <option key={option.label} value="" disabled>
                          {option.label}
                        </option>
                      )
                    )}
                  </select>
                </div>
                <MediaField
                  id="image"
                  label="Image"
                  file={formData.image}
                  existingUrl={existingImage}
                  maxMB={MAX_IMAGE_MB}
                  onFileSelect={(file) => setFormData({ ...formData, image: file })}
                />

                <div className="form-buttons">
                  <button type="submit" className="submit-btn">
                    {editingId ? "Update" : "Add"}
                  </button>
                  <button
                    type="button"
                    onClick={closeModal}
                    className="cancel-btn"
                  >
                    Cancel
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* List Table */}
        <table className="variety-table">
          <thead>
            <tr>
              <th>Collection Name</th>
              <th>Category</th>
              <th>Route To</th>
              <th>Image</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {newArrivals.map((newarrival) => (
              <tr key={newarrival._id}>
                <td>{newarrival.collectionName}</td>
                <td>{formatText(newarrival.category)}</td>
                <td>{formatText(newarrival.routeTo)}</td>
                <td>
                  <div className="media-cell">
                    <img src={newarrival.image} alt={newarrival.collectionName} />
                    <DownloadButton url={newarrival.image} />
                  </div>
                </td>
                <td>
                  <button onClick={() => handleEdit(newarrival)}>Edit</button>
                  {isAdmin() && (
                    <button onClick={() => handleDelete(newarrival._id)}>
                      Delete
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Confirmation Popup */}
      {showConfirm && (
        <div className="confirm-popup">
          <div className="confirm-content">
            <p>Are you sure you want to delete this arrival?</p>
            <div className="popup-actions">
              <button
                onClick={() => confirmDelete(selectedVideoId)}
                className="confirm-btn"
              >
                Confirm
              </button>
              <button
                onClick={() => setShowConfirm(false)}
                className="cancel-btn"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
      <ToastContainer />
    </>
  );
};

export default NewArrivals;
